import { useState } from 'react';

interface Recap {
  endurance: number;
  appraisal: number;
  sleep: {
    quality: string;
    duration: number;
    score: number;
  };
  nutrition: {
    breakfast: {
      completed: boolean;
      grade: string;
      notes?: string;
    };
    lunch: {
      completed: boolean;
      grade: string;
      notes?: string;
    };
    dinner: {
      completed: boolean;
      grade: string;
      notes?: string;
    };
    snacks: {
      completed: boolean;
      grade: string;
      notes?: string;
    };
    hydration: {
      current: number;
      target: number;
      percentage: number;
    };
  };
  workouts: {
    completed: boolean;
    intensity: number;
    duration: number;
  };
  productivity: {
    tasksCompleted: number;
    totalTasks: number;
    score: number;
  };
}

// Mock data for development
const recap: Recap = {
  endurance: 75,
  appraisal: 82,
  sleep: {
    quality: "Good",
    duration: 7.5,
    score: 85
  },
  nutrition: {
    breakfast: {
      completed: true,
      grade: "A",
      notes: "Balanced meal with protein and fiber"
    },
    lunch: {
      completed: true,
      grade: "B",
      notes: "Good portions, could use more vegetables"
    },
    dinner: {
      completed: true,
      grade: "A",
      notes: "Excellent balance of nutrients"
    },
    snacks: {
      completed: true,
      grade: "C",
      notes: "Consider healthier alternatives"
    },
    hydration: {
      current: 2100,
      target: 3000,
      percentage: 70
    }
  },
  workouts: {
    completed: true,
    intensity: 7,
    duration: 45
  },
  productivity: {
    tasksCompleted: 8,
    totalTasks: 10,
    score: 80
  }
};

const getGradeColor = (grade: string) => {
  const colors = {
    'A': 'text-green-500',
    'B': 'text-blue-500',
    'C': 'text-yellow-500',
    'D': 'text-orange-500',
    'F': 'text-red-500'
  };
  return colors[grade as keyof typeof colors] || 'text-gray-500';
};

export default function UserRecaps() {
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="border border-border rounded-lg p-4 space-y-6 bg-background">
      {/* Date Display */}
      <div className="flex justify-between items-center border-b border-border pb-3">
        <h2 className="text-sm font-medium text-muted-foreground">{currentDate}</h2>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Vertical Meters */}
        <div className="col-span-2 space-y-4">
          {/* Endurance Meter */}
          <div className="h-40 relative bg-gradient-to-t from-blue-950 to-blue-900 rounded-lg p-2 flex flex-col justify-between">
            <div className="absolute bottom-0 left-0 w-full bg-blue-800/20 rounded-lg transition-all duration-500"
                 style={{ height: `${recap.endurance}%` }}>
              <div className="h-full w-full bg-gradient-to-t from-blue-500/20 to-blue-400/20" />
            </div>
            <span className="text-xs text-blue-100 font-medium relative z-10">Endurance</span>
            <span className="text-sm text-blue-100 font-bold relative z-10">{recap.endurance}%</span>
          </div>

          {/* Appraisal Meter */}
          <div className="h-40 relative bg-gradient-to-t from-purple-950 to-purple-900 rounded-lg p-2 flex flex-col justify-between">
            <div className="absolute bottom-0 left-0 w-full bg-purple-800/20 rounded-lg transition-all duration-500"
                 style={{ height: `${recap.appraisal}%` }}>
              <div className="h-full w-full bg-gradient-to-t from-purple-500/20 to-purple-400/20" />
            </div>
            <span className="text-xs text-purple-100 font-medium relative z-10">Appraisal</span>
            <span className="text-sm text-purple-100 font-bold relative z-10">{recap.appraisal}%</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="col-span-10 grid grid-cols-2 gap-4">
          {/* Sleep Section */}
          <div className="bg-secondary/50 rounded-lg p-3">
            <h3 className="text-sm font-semibold mb-2">Sleep</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Quality</span>
                <span className="text-xs font-medium">{recap?.sleep?.quality}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Duration</span>
                <span className="text-xs font-medium">{recap?.sleep?.duration}hrs</span>
              </div>
              <div className="relative h-1.5 bg-secondary rounded-full">
                <div
                  className="absolute top-0 left-0 h-full bg-indigo-500 rounded-full"
                  style={{ width: `${recap?.sleep?.score}%` }}
                />
              </div>
            </div>
          </div>

          {/* Workout Section */}
          <div className="bg-secondary/50 rounded-lg p-3">
            <h3 className="text-sm font-semibold mb-2">Workout</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Status</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  recap?.workouts?.completed
                    ? 'bg-green-500/20 text-green-500'
                    : 'bg-red-500/20 text-red-500'
                }`}>
                  {recap?.workouts?.completed ? 'Completed' : 'Missed'}
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Intensity</span>
                  <div className="flex space-x-0.5">
                    {Array.isArray([...Array(10)]) && Array.isArray([...Array(10)]) && Array.isArray([...Array(10)]) && [...Array(10)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-4 rounded-sm ${
                          i < recap?.workouts?.intensity
                            ? 'bg-orange-500'
                            : 'bg-secondary'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Duration</span>
                  <span className="text-xs font-medium">{recap?.workouts?.duration} min</span>
                </div>
              </div>
            </div>
          </div>

          {/* Nutrition Section (Double Height) */}
          <div className="bg-secondary/50 rounded-lg p-3 row-span-2">
            <h3 className="text-sm font-semibold mb-2">Nutrition</h3>
            <div className="space-y-3">
              {/* Meals */}
              <div className="space-y-2">
                {Object.entries(recap.nutrition).map(([meal, data]) => {
                  if (meal === 'hydration') return null;
                  const { completed, grade, notes } = data as { completed: boolean; grade: string; notes?: string };
                  return (
                    <div key={meal} className="flex items-center justify-between group">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${completed ? 'bg-green-500' : 'bg-secondary'}`} />
                        <span className="text-xs text-muted-foreground capitalize">{meal}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs font-bold ${getGradeColor(grade)}`}>{grade}</span>
                        {notes && (
                          <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity absolute bg-background/90 p-1 rounded right-full mr-2 whitespace-nowrap">
                            {notes}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Hydration Progress */}
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Hydration</span>
                  <span className="text-xs font-medium">
                    {recap?.nutrition?.hydration.current}ml / {recap?.nutrition?.hydration.target}ml
                  </span>
                </div>
                <div className="relative h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${recap?.nutrition?.hydration.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Productivity Section */}
          <div className="bg-secondary/50 rounded-lg p-3">
            <h3 className="text-sm font-semibold mb-2">Productivity</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Tasks</span>
                <span className="text-xs font-medium">
                  {recap?.productivity?.tasksCompleted}/{recap?.productivity?.totalTasks}
                </span>
              </div>
              <div className="relative h-1.5 bg-secondary rounded-full">
                <div
                  className="absolute top-0 left-0 h-full bg-green-500 rounded-full"
                  style={{
                    width: `${(recap?.productivity?.tasksCompleted /
                      recap?.productivity?.totalTasks) *
                      100}%`,
                  }}
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Score</span>
                <span className="text-xs font-medium">{recap?.productivity?.score}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}